import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SelectDropdown from '../components/SelectDropdown';
import { toast } from 'react-toastify';
import '../styles/pages/_consultant-wellness-module-creation.scss';

interface TopicFolder {
  id: string;
  title: string;
  description: string;
}

interface WellnessModule {
  id?: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  content: string;
  lessonCount: number;
  estimatedDuration: number;
  objectives: string[];
  tags: string[];
  topicFolder?: TopicFolder;
}

const ConsultantWellnessModuleCreationPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { token, user } = useAuth();

  const [module, setModule] = useState<WellnessModule>({
    title: '',
    description: '',
    category: 'physical',
    difficulty: 'Beginner',
    content: '',
    lessonCount: 1,
    estimatedDuration: 30,
    objectives: [''],
    tags: [''],
    topicFolder: {
      id: `topic_${Date.now()}`,
      title: '',
      description: ''
    }
  });

  const [isPublishing, setIsPublishing] = useState(false);
  const [completionPercent, setCompletionPercent] = useState(0);
  const [isPartOfTopic, setIsPartOfTopic] = useState(true);

  // Calculate completion percentage
  useEffect(() => {
    const requiredFields = [module.title, module.description, module.category, module.content];
    const filledFields = requiredFields.filter(field => field && field.trim().length > 0).length;
    const percent = Math.round((filledFields / requiredFields.length) * 100);
    setCompletionPercent(percent);
  }, [module]);

  const handleFieldChange = (field: keyof WellnessModule, value: any) => {
    setModule(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleObjectiveChange = (index: number, value: string) => {
    const newObjectives = [...module.objectives];
    newObjectives[index] = value;
    setModule(prev => ({
      ...prev,
      objectives: newObjectives
    }));
  };

  const handleAddObjective = () => {
    setModule(prev => ({
      ...prev,
      objectives: [...prev.objectives, '']
    }));
  };

  const handleRemoveObjective = (index: number) => {
    setModule(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }));
  };

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...module.tags];
    newTags[index] = value;
    setModule(prev => ({
      ...prev,
      tags: newTags
    }));
  };

  const handleAddTag = () => {
    setModule(prev => ({
      ...prev,
      tags: [...prev.tags, '']
    }));
  };

  const handleRemoveTag = (index: number) => {
    setModule(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handlePublish = async () => {
    // Validation
    if (!module.title.trim()) {
      toast.error('Module title is required');
      return;
    }
    if (!module.description.trim()) {
      toast.error('Module description is required');
      return;
    }
    if (!module.content.trim()) {
      toast.error('Module content is required');
      return;
    }
    if (isPartOfTopic && !module.topicFolder?.title.trim()) {
      toast.error('Please provide a topic folder name');
      return;
    }

    setIsPublishing(true);

    try {
      const TOKEN = token || localStorage.getItem('token');
      if (!TOKEN) {
        toast.error('Authentication required');
        return;
      }

      if (!user?.id) {
        toast.error('User information not available');
        return;
      }

      const payload = {
        title: module.title,
        description: module.description,
        category: module.category,
        difficultyLevel: module.difficulty,
        duration: module.estimatedDuration,
        sessions: []
        // Extra fields stored locally but not sent to backend
        // content, lessonCount, objectives, tags, topicFolder are stored for future use
      };

      const response = await fetch('/api/wellness-modules/publish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${TOKEN}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.message || errorData.error || 'Failed to publish module';
        throw new Error(errorMsg);
      }

      const data = await response.json();
      toast.success('Module published successfully!');
      localStorage.removeItem('wellness-module-draft');
      navigate('/panel/consultant/wellness');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to publish module';
      toast.error(errorMsg);
      console.error('Publish error:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraft = () => {
    // Save to localStorage for now
    localStorage.setItem('wellness-module-draft', JSON.stringify(module));
    toast.success('Module draft saved');
  };

  const handleCancel = () => {
    if (window.confirm('Discard changes and go back?')) {
      navigate('/panel/consultant/wellness');
    }
  };

  return (
    <div className={`wellness-module-creation ${isDarkMode ? 'dark' : ''}`}>
      <div className="wellness-module-creation__container">
        {/* Header */}
        <div className="wellness-module-creation__header">
          <div>
            <h1 className="wellness-module-creation__title">Create Wellness Module</h1>
            <p className="wellness-module-creation__subtitle">Design an educational module for your consultants</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="wellness-module-creation__progress">
          <div className="wellness-module-creation__progress-header">
            <span className="wellness-module-creation__progress-label">Module Progress</span>
            <span className="wellness-module-creation__progress-percent">{completionPercent}% Complete</span>
          </div>
          <progress
            className="wellness-module-creation__progress-bar"
            value={completionPercent}
            max="100"
          />
        </div>

        {/* Form Sections */}
        <div className="wellness-module-creation__content">
          {/* Topic Folder Section */}
          <section className="wellness-module-creation__section">
            <div className="wellness-module-creation__section-header">
              <h2 className="wellness-module-creation__section-title">
                <span className="material-symbols-outlined">folder</span>
                Topic Folder
              </h2>
            </div>

            <div className="wellness-module-creation__form-grid">
              <div className="form-control" style={{ gridColumn: '1 / -1', marginBottom: '1rem' }}>
                <label className="form-control__label">
                  <input
                    type="checkbox"
                    checked={isPartOfTopic}
                    onChange={(e) => setIsPartOfTopic(e.target.checked)}
                    style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  This module is part of a topic folder
                </label>
              </div>
            </div>

            {isPartOfTopic && module.topicFolder && (
              <div className="wellness-module-creation__form-grid">
                <div className="form-control">
                  <label className="form-control__label">
                    Topic Folder Name <span className="wellness-module-creation__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Healthy Eating Habits"
                    value={module.topicFolder.title}
                    onChange={(e) =>
                      setModule(prev => ({
                        ...prev,
                        topicFolder: {
                          ...prev.topicFolder!,
                          title: e.target.value
                        }
                      }))
                    }
                  />
                </div>

                <div className="form-control" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-control__label">Topic Description</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe what this topic folder contains..."
                    rows={2}
                    value={module.topicFolder.description}
                    onChange={(e) =>
                      setModule(prev => ({
                        ...prev,
                        topicFolder: {
                          ...prev.topicFolder!,
                          description: e.target.value
                        }
                      }))
                    }
                  />
                </div>
              </div>
            )}
          </section>

          {/* Basic Info Section */}
          <section className="wellness-module-creation__section">
            <div className="wellness-module-creation__section-header">
              <h2 className="wellness-module-creation__section-title">
                <span className="material-symbols-outlined">info</span>
                Module Information
              </h2>
            </div>

            <div className="wellness-module-creation__form-grid">
              <div className="form-control">
                <label className="form-control__label">
                  Module Title <span className="wellness-module-creation__required">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Stress Management Fundamentals"
                  value={module.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Category</label>
                <SelectDropdown
                  value={module.category}
                  onChange={(value) => handleFieldChange('category', value)}
                  options={[
                    { value: 'physical', label: 'Physical' },
                    { value: 'financial', label: 'Financial' },
                    { value: 'spiritual', label: 'Spiritual' },
                    { value: 'nutritional', label: 'Nutritional' },
                    { value: 'emotio-psycho-rational', label: 'Emotio-Psycho-Rational' },
                    { value: 'legendarial', label: 'Legendarial' },
                    { value: 'occupational', label: 'Occupational' },
                    { value: 'religio-traditio-cultural', label: 'Religio-Traditio-Cultural' },
                    { value: 'socio-relational', label: 'Socio-Relational' },
                    { value: 'environmental', label: 'Environmental' }
                  ]}
                  placeholder="Select category"
                  ariaLabel="Module category"
                />
              </div>

              <div className="wellness-module-creation__form-grid--single">
                <div className="form-control">
                  <label className="form-control__label">
                    Description <span className="wellness-module-creation__required">*</span>
                  </label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe what the module covers and its benefits..."
                    rows={4}
                    value={module.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Difficulty Level</label>
                <div className="wellness-module-creation__difficulty-selector">
                  {(['Beginner', 'Intermediate', 'Advanced'] as const).map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={`wellness-module-creation__difficulty-btn ${
                        module.difficulty === level ? 'wellness-module-creation__difficulty-btn--active' : ''
                      }`}
                      onClick={() => handleFieldChange('difficulty', level)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Number of Lessons</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="100"
                  value={module.lessonCount}
                  onChange={(e) => handleFieldChange('lessonCount', parseInt(e.target.value) || 1)}
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Estimated Duration (minutes)</label>
                <input
                  type="number"
                  className="form-input"
                  min="5"
                  value={module.estimatedDuration}
                  onChange={(e) => handleFieldChange('estimatedDuration', parseInt(e.target.value) || 30)}
                />
              </div>
            </div>
          </section>

          {/* Content Section */}
          <section className="wellness-module-creation__section">
            <div className="wellness-module-creation__section-header">
              <h2 className="wellness-module-creation__section-title">
                <span className="material-symbols-outlined">description</span>
                Module Content
              </h2>
            </div>

            <div className="form-control">
              <label className="form-control__label">
                Content <span className="wellness-module-creation__required">*</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="Write the main content of your module here..."
                rows={8}
                value={module.content}
                onChange={(e) => handleFieldChange('content', e.target.value)}
              />
            </div>
          </section>

          {/* Learning Objectives Section */}
          <section className="wellness-module-creation__section">
            <div className="wellness-module-creation__section-header">
              <h2 className="wellness-module-creation__section-title">
                <span className="material-symbols-outlined">check_circle</span>
                Learning Objectives
              </h2>
            </div>

            <div className="wellness-module-creation__list">
              {module.objectives.map((objective, index) => (
                <div key={index} className="wellness-module-creation__list-item">
                  <span className="wellness-module-creation__list-number">{index + 1}</span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Understand the basics of meditation"
                    value={objective}
                    onChange={(e) => handleObjectiveChange(index, e.target.value)}
                  />
                  {module.objectives.length > 1 && (
                    <button
                      type="button"
                      className="wellness-module-creation__remove-btn"
                      onClick={() => handleRemoveObjective(index)}
                      aria-label="Remove objective"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="wellness-module-creation__add-list-item"
                onClick={handleAddObjective}
              >
                <span className="material-symbols-outlined">add</span>
                Add Objective
              </button>
            </div>
          </section>

          {/* Tags Section */}
          <section className="wellness-module-creation__section">
            <div className="wellness-module-creation__section-header">
              <h2 className="wellness-module-creation__section-title">
                <span className="material-symbols-outlined">label</span>
                Tags (optional)
              </h2>
            </div>

            <div className="wellness-module-creation__list">
              {module.tags.map((tag, index) => (
                <div key={index} className="wellness-module-creation__tag-item">
                  <span className="material-symbols-outlined wellness-module-creation__tag-icon">label</span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., breathing, relaxation"
                    value={tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                  />
                  {module.tags.length > 1 && (
                    <button
                      type="button"
                      className="wellness-module-creation__remove-btn"
                      onClick={() => handleRemoveTag(index)}
                      aria-label="Remove tag"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="wellness-module-creation__add-list-item"
                onClick={handleAddTag}
              >
                <span className="material-symbols-outlined">add</span>
                Add Tag
              </button>
            </div>
          </section>

          {/* Footer Actions */}
          <div className="wellness-module-creation__footer">
            <div className="wellness-module-creation__footer-secondary">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSaveDraft}
              >
                <span className="material-symbols-outlined">save</span>
                Save Draft
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                <span className="material-symbols-outlined">close</span>
                Cancel
              </button>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePublish}
              disabled={isPublishing}
            >
              <span className="material-symbols-outlined">
                {isPublishing ? 'hourglass_empty' : 'rocket_launch'}
              </span>
              {isPublishing ? 'Publishing...' : 'Publish Module'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantWellnessModuleCreationPage;
